import React, { useState } from 'react';
import { Calendar, Clock, Users } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reservation = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '2',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/reservations`, formData);
      
      toast({
        title: "Reservation Received!",
        description: "We'll contact you shortly to confirm your booking.",
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        date: '',
        time: '',
        guests: '2',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting reservation:', error);
      toast({
        title: "Error",
        description: "Failed to submit reservation. Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="reservation" className="py-24 bg-zinc-950">
      <div className="max-w-3xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-zinc-500 text-sm tracking-widest mb-4 uppercase">Book Your Table</p>
          <h2 className="text-5xl md:text-6xl font-light text-white mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Make a Reservation</h2>
          <div className="w-16 h-px bg-zinc-700 mx-auto mb-6"></div>
          <p className="text-zinc-400 max-w-2xl mx-auto font-light">
            Reserve your table today and enjoy an unforgettable dining experience
          </p>
        </div>

        {/* Reservation Form */}
        <div className="bg-black p-10 border border-zinc-800">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-zinc-400 mb-2 font-light text-sm tracking-wide">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 text-white focus:outline-none focus:border-white transition-colors font-light disabled:opacity-50"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-zinc-400 mb-2 font-light text-sm tracking-wide">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 text-white focus:outline-none focus:border-white transition-colors font-light disabled:opacity-50"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-zinc-400 mb-2 font-light text-sm tracking-wide">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 text-white focus:outline-none focus:border-white transition-colors font-light disabled:opacity-50"
                  placeholder="+358 41 123 4567"
                />
              </div>
              <div>
                <label htmlFor="guests" className="block text-zinc-400 mb-2 font-light text-sm tracking-wide">
                  <Users className="inline mr-2" size={14} strokeWidth={1.5} />
                  Number of Guests *
                </label>
                <select
                  id="guests"
                  name="guests"
                  value={formData.guests}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 text-white focus:outline-none focus:border-white transition-colors font-light disabled:opacity-50"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <option key={num} value={num}>{num} {num === 1 ? 'Guest' : 'Guests'}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="date" className="block text-zinc-400 mb-2 font-light text-sm tracking-wide">
                  <Calendar className="inline mr-2" size={14} strokeWidth={1.5} />
                  Date *
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 text-white focus:outline-none focus:border-white transition-colors font-light disabled:opacity-50"
                />
              </div>
              <div>
                <label htmlFor="time" className="block text-zinc-400 mb-2 font-light text-sm tracking-wide">
                  <Clock className="inline mr-2" size={14} strokeWidth={1.5} />
                  Time *
                </label>
                <select
                  id="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 text-white focus:outline-none focus:border-white transition-colors font-light disabled:opacity-50"
                >
                  <option value="">Select Time</option>
                  <option value="11:00">11:00 AM</option>
                  <option value="11:30">11:30 AM</option>
                  <option value="12:00">12:00 PM</option>
                  <option value="12:30">12:30 PM</option>
                  <option value="13:00">1:00 PM</option>
                  <option value="13:30">1:30 PM</option>
                  <option value="14:00">2:00 PM</option>
                  <option value="14:30">2:30 PM</option>
                  <option value="17:00">5:00 PM</option>
                  <option value="17:30">5:30 PM</option>
                  <option value="18:00">6:00 PM</option>
                  <option value="18:30">6:30 PM</option>
                  <option value="19:00">7:00 PM</option>
                  <option value="19:30">7:30 PM</option>
                  <option value="20:00">8:00 PM</option>
                  <option value="20:30">8:30 PM</option>
                  <option value="21:00">9:00 PM</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-zinc-400 mb-2 font-light text-sm tracking-wide">
                Special Requests (Optional)
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                disabled={isSubmitting}
                rows={4}
                className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 text-white focus:outline-none focus:border-white transition-colors resize-none font-light disabled:opacity-50"
                placeholder="Any special requests or dietary requirements?"
              ></textarea>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-10 py-4 bg-white hover:bg-zinc-200 text-black font-normal tracking-wider uppercase text-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Reserve Table'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Reservation;